/** Selection list with optional sections. Wraps React Aria ListBox. */
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxSection as AriaListBoxSection,
  Header,
  type ListBoxProps as AriaListBoxProps,
  type ListBoxItemProps as AriaListBoxItemProps,
  type SectionProps as AriaSectionProps,
} from 'react-aria-components';
import { Check } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

interface ListBoxProps<T extends object> extends AriaListBoxProps<T> {
  className?: string;
}

export function ListBox<T extends object>({ className, ...props }: ListBoxProps<T>) {
  return (
    <AriaListBox
      className={cn(
        'outline-none p-1 rounded-xl border border-[var(--f-border-strong)] bg-[var(--f-bg-raised)]',
        className
      )}
      {...props}
    />
  );
}

interface ListBoxItemProps extends AriaListBoxItemProps {
  className?: string;
}

export function ListBoxItem({ className, children, ...props }: ListBoxItemProps) {
  return (
    <AriaListBoxItem
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer outline-none text-sm',
        'hover:bg-[var(--f-bg-sunken)] focus:bg-[var(--f-bg-sunken)]',
        'data-[selected]:bg-[var(--f-accent-subtle)] data-[selected]:text-[var(--f-accent-text)]',
        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
        'transition-colors',
        className
      )}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <span className="flex-1">{children as React.ReactNode}</span>
          {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
        </>
      )}
    </AriaListBoxItem>
  );
}

interface ListBoxSectionProps<T extends object> extends AriaSectionProps<T> {
  title?: string;
  className?: string;
}

export function ListBoxSection<T extends object>({
  title,
  className,
  ...props
}: ListBoxSectionProps<T>) {
  return (
    <AriaListBoxSection className={className} {...props}>
      {title && (
        <Header className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 text-[var(--f-fg-muted)]">
          {title}
        </Header>
      )}
    </AriaListBoxSection>
  );
}
